import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Clock,
  RotateCcw,
  Search,
  ExternalLink,
  User,
  Palette,
  ShieldCheck,
  RefreshCw,
  Layers,
  Package,
  AlertTriangle,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { compactMoney } from "../../lib/format";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import Pagination, { usePagination } from "./Pagination";
import { useSavedState } from "../../lib/listState";

export default function Approvals({ orders = [], clients = [], onOpen, onOpenDesign, onChange, user, routeParams }) {
  const [approvalsList, setApprovalsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useSavedState("approvals", "activeTab", routeParams?.activeTab || routeParams?.tab || "pending");
  const [search, setSearch] = useSavedState("approvals", "search", "");
  const [statusFilter, setStatusFilter] = useSavedState("approvals", "statusFilter", "All");
  const [typeFilter, setTypeFilter] = useSavedState("approvals", "typeFilter", "All");
  const [companyFilter, setCompanyFilter] = useState(routeParams?.company || "");

  useEffect(() => {
    if (routeParams) {
      if (routeParams.activeTab || routeParams.tab) {
        setActiveTab(routeParams.activeTab || routeParams.tab);
      }
      if (routeParams.company !== undefined) {
        setCompanyFilter(routeParams.company || "");
      }
    }
  }, [routeParams, setActiveTab]);

  const [busyAction, setBusyAction] = useState("");

  // Bulk Selection and Modal states
  const [selectedRequestIds, setSelectedRequestIds] = useState(new Set());
  const [bulkApproveModalOpen, setBulkApproveModalOpen] = useState(false);
  const [bulkRejectModalOpen, setBulkRejectModalOpen] = useState(false);
  const [bulkComments, setBulkComments] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [isSubmittingBulk, setIsSubmittingBulk] = useState(false);

  const isManagement = isAdmin(user);
  const canAct = isAdmin(user) || canAccessDepartment(user, ["Sales", "Design", "Management"]);

  // Load approvals history from backend
  const loadApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api("get", "/approvals");
      setApprovalsList(res.data || []);
    } catch (err) {
      console.error("Failed to load approvals:", err);
      toast.error("Could not load approvals");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  // 1. Eligible orders pending approval:
  // Must be explicitly submitted and currently in "Awaiting Approval" stage
  const pendingOrders = useMemo(() => {
    return (orders || []).filter((o) => {
      if (o.deleted) return false;
      if (companyFilter && companyFilter !== "All Companies") {
        if ((o.company || "").toLowerCase() !== companyFilter.toLowerCase()) return false;
      }
      // Must not already be approved
      if (o.approval_status === "Approved") return false;

      // If order has design_stage, only "Awaiting Approval" qualifies
      if (o.design_stage) {
        return o.design_stage === "Awaiting Approval";
      }

      // If no design_stage (legacy / non-design orders):
      return (
        ["client approval", "approval"].includes(String(o.current_stage || "").toLowerCase()) ||
        o.approval_status === "Awaiting Approval"
      );
    });
  }, [orders, companyFilter]);

  // 2. Group into approval requests:
  // Multiple products belonging to the same bulk batch are grouped into ONE request.
  // Independent orders remain separate independent requests.
  const pendingRequests = useMemo(() => {
    const batchMap = new Map();
    const list = [];

    for (const o of pendingOrders) {
      const batchId = o.upload_batch_id;
      if (batchId) {
        if (!batchMap.has(batchId)) {
          const group = {
            id: batchId,
            isBulk: true,
            batchId: batchId,
            batchName: o.upload_batch_name || "",
            client_name: o.client_name || "—",
            client_id: o.client_id || "",
            client_contact_person: o.client_contact_person || "",
            client_phone: o.client_phone || "",
            client_email: o.client_email || "",
            company: o.company || "",
            assigned_designer: o.assigned_designer || "",
            design_version: o.design_version || 1,
            required_delivery_date: o.required_delivery_date || "",
            order_date: o.order_date || "",
            priority: o.priority || "Normal",
            approval_status: o.approval_status || "Awaiting Approval",
            orders: [],
            total_value: 0,
            total_quantity: 0,
          };
          batchMap.set(batchId, group);
          list.push(group);
        }
        const group = batchMap.get(batchId);
        group.orders.push(o);
        group.total_value += (o.total_value || 0);
        group.total_quantity += (o.quantity || 0);
        if (o.design_version && o.design_version > group.design_version) {
          group.design_version = o.design_version;
        }
        if (o.priority === "Urgent" || (o.priority === "High" && group.priority !== "Urgent")) {
          group.priority = o.priority;
        }
        if (o.required_delivery_date && (!group.required_delivery_date || o.required_delivery_date < group.required_delivery_date)) {
          group.required_delivery_date = o.required_delivery_date;
        }
        if (!group.assigned_designer && o.assigned_designer) {
          group.assigned_designer = o.assigned_designer;
        }
      } else {
        list.push({
          id: o.order_id,
          isBulk: false,
          order_id: o.order_id,
          client_name: o.client_name || "—",
          client_id: o.client_id || "",
          client_contact_person: o.client_contact_person || "",
          client_phone: o.client_phone || "",
          client_email: o.client_email || "",
          company: o.company || "",
          product: o.product || "—",
          quantity: o.quantity || 0,
          unit: o.unit || "units",
          total_value: o.total_value || 0,
          design_version: o.design_version || 1,
          assigned_designer: o.assigned_designer || "",
          required_delivery_date: o.required_delivery_date || "",
          order_date: o.order_date || "",
          priority: o.priority || "Normal",
          approval_status: o.approval_status || "Awaiting Approval",
          orders: [o],
          total_quantity: o.quantity || 0,
        });
      }
    }

    return list;
  }, [pendingOrders]);

  // 3. Filtered pending requests based on search & filter
  const filteredPending = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pendingRequests.filter((req) => {
      if (statusFilter !== "All" && req.approval_status !== statusFilter) return false;
      if (!q) return true;
      if ((req.id || "").toLowerCase().includes(q)) return true;
      if ((req.client_name || "").toLowerCase().includes(q)) return true;
      if ((req.assigned_designer || "").toLowerCase().includes(q)) return true;
      if (req.isBulk) {
        if ((req.batchName || "").toLowerCase().includes(q)) return true;
        return req.orders.some(
          (o) =>
            (o.product || "").toLowerCase().includes(q) ||
            (o.order_id || "").toLowerCase().includes(q)
        );
      } else {
        return (
          (req.product || "").toLowerCase().includes(q) ||
          (req.order_id || "").toLowerCase().includes(q)
        );
      }
    });
  }, [pendingRequests, search, statusFilter]);

  // 4. Filtered approvals history based on search & filter
  const filteredHistory = useMemo(() => {
    const q = search.trim().toLowerCase();
    return approvalsList.filter((a) => {
      if (statusFilter !== "All") {
        if (statusFilter === "Approved" && a.status !== "Approved") return false;
        if (
          (statusFilter === "Revision Required" || statusFilter === "Not Approved") &&
          a.status !== "Revision Required" &&
          a.status !== "Not Approved" &&
          a.status !== "Rejected"
        )
          return false;
      }
      if (typeFilter !== "All" && a.approval_type !== typeFilter) return false;
      if (!q) return true;
      return (
        (a.order_id || "").toLowerCase().includes(q) ||
        (a.upload_batch_id || "").toLowerCase().includes(q) ||
        (a.approval_id || "").toLowerCase().includes(q) ||
        (a.client_name || "").toLowerCase().includes(q) ||
        (a.product || "").toLowerCase().includes(q) ||
        (a.approver || "").toLowerCase().includes(q) ||
        (a.comments || "").toLowerCase().includes(q)
      );
    });
  }, [approvalsList, search, statusFilter, typeFilter]);

  const pendingPagination = usePagination(filteredPending, 10, { sectionKey: "approvals_pending" });
  const historyPagination = usePagination(filteredHistory, 10, { sectionKey: "approvals_history" });

  // Summary Metrics calculations
  const totalPendingValue = useMemo(() => {
    return pendingOrders.reduce((acc, o) => acc + (o.total_value || 0), 0);
  }, [pendingOrders]);

  const approvedCount = useMemo(() => {
    return approvalsList.filter((a) => a.status === "Approved").length;
  }, [approvalsList]);

  const revisionsCount = useMemo(() => {
    return approvalsList.filter((a) =>
      ["Revision Required", "Not Approved", "Rejected"].includes(a.status)
    ).length;
  }, [approvalsList]);

  // Execute Approve decision for single order
  const handleApproveOrder = async (orderId) => {
    setBusyAction(orderId);
    try {
      await api("post", `/orders/${orderId}/approvals`, {
        status: "Approved",
        approver: user?.name || "Authorized Approver",
        comments: "Design Approved",
      });
      toast.success(`Order ${orderId} design approved — Production unlocked!`);
      await loadApprovals();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not approve design";
      toast.error(typeof msg === "string" ? msg : "Approval failed");
    } finally {
      setBusyAction("");
    }
  };

  // Execute Reject decision for single order
  const handleRejectOrder = async (orderId) => {
    setBusyAction(orderId);
    try {
      await api("post", `/orders/${orderId}/approvals`, {
        status: "Not Approved",
        approver: user?.name || "Authorized Approver",
        comments: "Revision requested by reviewer",
      });
      toast.success(`Order ${orderId} returned for revisions (In Progress)`);
      await loadApprovals();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not reject design";
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally {
      setBusyAction("");
    }
  };

  // Execute Approve decision for Grouped Batch (applies to ALL products)
  const handleApproveBatch = async (batchId) => {
    setBusyAction(batchId);
    try {
      await api("post", `/orders/batch/${batchId}/approvals`, {
        status: "Approved",
        approver: user?.name || "Authorized Approver",
        comments: "Bulk Design Approved",
      });
      toast.success(`Bulk Request #${batchId} approved (All products) — Production unlocked!`);
      await loadApprovals();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not approve bulk request";
      toast.error(typeof msg === "string" ? msg : "Approval failed");
    } finally {
      setBusyAction("");
    }
  };

  // Execute Reject decision for Grouped Batch (returns ALL products for revisions)
  const handleRejectBatch = async (batchId) => {
    setBusyAction(batchId);
    try {
      await api("post", `/orders/batch/${batchId}/approvals`, {
        status: "Not Approved",
        approver: user?.name || "Authorized Approver",
        comments: "Bulk Design Revision Requested",
      });
      toast.success(`Bulk Request #${batchId} returned for revisions (In Progress)`);
      await loadApprovals();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not reject bulk request";
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally {
      setBusyAction("");
    }
  };

  // Reset selection when changing tabs
  useEffect(() => {
    setSelectedRequestIds(new Set());
  }, [activeTab]);

  // Prune any selected IDs that are no longer in pendingRequests
  useEffect(() => {
    setSelectedRequestIds((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(pendingRequests.map((r) => r.id));
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (validIds.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [pendingRequests]);

  // Selected requests objects list
  const selectedRequestsList = useMemo(() => {
    return pendingRequests.filter((r) => selectedRequestIds.has(r.id));
  }, [pendingRequests, selectedRequestIds]);

  // Products count in selected requests
  const selectedProductsCount = useMemo(() => {
    return selectedRequestsList.reduce((sum, r) => sum + (r.orders?.length || 1), 0);
  }, [selectedRequestsList]);

  // Total value in selected requests
  const selectedTotalValue = useMemo(() => {
    return selectedRequestsList.reduce((sum, r) => sum + (r.total_value || 0), 0);
  }, [selectedRequestsList]);

  // Is every visible item in filteredPending selected?
  const isAllVisibleSelected = useMemo(() => {
    if (filteredPending.length === 0) return false;
    return filteredPending.every((r) => selectedRequestIds.has(r.id));
  }, [filteredPending, selectedRequestIds]);

  const hasSomeVisibleSelected = useMemo(() => {
    return filteredPending.some((r) => selectedRequestIds.has(r.id)) && !isAllVisibleSelected;
  }, [filteredPending, selectedRequestIds, isAllVisibleSelected]);

  // Toggle single request selection
  const handleToggleSelect = (reqId) => {
    if (!canAct) return;
    setSelectedRequestIds((prev) => {
      const next = new Set(prev);
      if (next.has(reqId)) next.delete(reqId);
      else next.add(reqId);
      return next;
    });
  };

  // Toggle select all visible requests
  const handleToggleSelectAll = () => {
    if (!canAct || filteredPending.length === 0) return;
    if (isAllVisibleSelected) {
      setSelectedRequestIds((prev) => {
        const next = new Set(prev);
        filteredPending.forEach((r) => next.delete(r.id));
        return next;
      });
    } else {
      setSelectedRequestIds((prev) => {
        const next = new Set(prev);
        filteredPending.forEach((r) => next.add(r.id));
        return next;
      });
    }
  };

  const handleClearSelection = () => {
    setSelectedRequestIds(new Set());
  };

  // Execute Bulk Approve for all selected requests
  const handleExecuteBulkApprove = async () => {
    if (selectedRequestIds.size === 0) return;
    setIsSubmittingBulk(true);
    try {
      const payload = {
        request_ids: Array.from(selectedRequestIds),
        status: "Approved",
        approver: user?.name || "Authorized Approver",
        comments: bulkComments.trim() || "Bulk Design Approved",
      };
      const res = await api("post", "/orders/bulk-approvals", payload);
      const data = res.data;
      if (data.failed_requests && data.failed_requests.length > 0) {
        toast.warning(
          `Approved ${data.successful_requests.length} requests (${data.products_affected} products). ${data.failed_requests.length} failed.`
        );
      } else {
        toast.success(
          `Successfully approved ${data.total_requests} requests (${data.products_affected} products) — Production unlocked!`
        );
      }
      setSelectedRequestIds(new Set());
      setBulkApproveModalOpen(false);
      setBulkComments("");
      await loadApprovals();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Bulk approval failed";
      toast.error(typeof msg === "string" ? msg : "Bulk approval failed");
    } finally {
      setIsSubmittingBulk(false);
    }
  };

  // Execute Bulk Reject for all selected requests
  const handleExecuteBulkReject = async () => {
    if (selectedRequestIds.size === 0) return;
    if (!rejectionReason.trim()) {
      toast.error("Please provide a rejection reason / revision note");
      return;
    }
    setIsSubmittingBulk(true);
    try {
      const payload = {
        request_ids: Array.from(selectedRequestIds),
        status: "Not Approved",
        approver: user?.name || "Authorized Approver",
        comments: rejectionReason.trim(),
      };
      const res = await api("post", "/orders/bulk-approvals", payload);
      const data = res.data;
      if (data.failed_requests && data.failed_requests.length > 0) {
        toast.warning(
          `Rejected ${data.successful_requests.length} requests. ${data.failed_requests.length} failed.`
        );
      } else {
        toast.success(
          `Returned ${data.total_requests} requests (${data.products_affected} products) to Design for revisions`
        );
      }
      setSelectedRequestIds(new Set());
      setBulkRejectModalOpen(false);
      setRejectionReason("");
      await loadApprovals();
      onChange && onChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Bulk rejection failed";
      toast.error(typeof msg === "string" ? msg : "Bulk rejection failed");
    } finally {
      setIsSubmittingBulk(false);
    }
  };

  return (
    <div className="content">
      {/* Header */}
      <div className="page-heading">
        <div>
          <div className="small-label">STAGE GATE · PRODUCTION GATEWAY</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h1 style={{ margin: 0 }}>Approvals Hub</h1>
            <span
              className="badge amber"
              style={{ fontSize: 13, padding: "3px 10px", fontWeight: 700 }}
              data-testid="badge-pending-count"
            >
              {pendingRequests.length} Pending {pendingRequests.length === 1 ? "Request" : "Requests"}
              {pendingOrders.length > pendingRequests.length ? ` (${pendingOrders.length} Products)` : ""}
            </span>
            {isManagement ? (
              <span className="badge blue" style={{ fontSize: 12, padding: "2px 8px" }}>
                🛡️ Management Authorized
              </span>
            ) : (
              <span className="badge purple" style={{ fontSize: 12, padding: "2px 8px" }}>
                🎨 Standard / Design Role
              </span>
            )}
          </div>
          <p className="muted" style={{ marginTop: 4 }}>
            Review and record Design Approvals (Yes / No). Production unlocks upon approval.
          </p>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            className="outline-btn compact"
            onClick={() => {
              loadApprovals();
              onChange && onChange();
            }}
            disabled={loading}
            title="Refresh Approvals"
          >
            <RefreshCw size={14} className={loading ? "spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="metrics-grid">
        <div
          className={`metric amber ${activeTab === "pending" ? "active" : ""}`}
          style={{ cursor: "pointer" }}
          onClick={() => setActiveTab("pending")}
          data-testid="metric-pending-approvals"
          title="Click to view Pending Review queue"
        >
          <div className="metric-top">
            <span>Pending Review</span>
            <Clock size={16} color="#d97706" />
          </div>
          <strong>{pendingRequests.length}</strong>
          <small>
            {pendingOrders.length === pendingRequests.length
              ? "Awaiting internal sign-off"
              : `${pendingOrders.length} products across ${pendingRequests.length} requests`}
          </small>
        </div>

        <div
          className={`metric green ${activeTab === "history" && statusFilter === "Approved" ? "active" : ""}`}
          style={{ cursor: "pointer" }}
          onClick={() => {
            setActiveTab("history");
            setStatusFilter("Approved");
          }}
          data-testid="metric-approved-count"
          title="Click to view Approved history"
        >
          <div className="metric-top">
            <span>Approved Records</span>
            <CheckCircle2 size={16} color="#059669" />
          </div>
          <strong>{approvedCount}</strong>
          <small>Production gate unlocked</small>
        </div>

        <div
          className={`metric purple ${activeTab === "history" && (statusFilter === "Revision Required" || statusFilter === "Not Approved") ? "active" : ""}`}
          style={{ cursor: "pointer" }}
          onClick={() => {
            setActiveTab("history");
            setStatusFilter("Revision Required");
          }}
          data-testid="metric-revisions-count"
          title="Click to view Revision history"
        >
          <div className="metric-top">
            <span>Revisions Logged</span>
            <RotateCcw size={16} color="#7c3aed" />
          </div>
          <strong>{revisionsCount}</strong>
          <small>Artwork changes requested</small>
        </div>

        <div className="metric blue" data-testid="metric-pending-value">
          <div className="metric-top">
            <span>Pending Value</span>
            <ShieldCheck size={16} color="#0284c7" />
          </div>
          <strong>{compactMoney(totalPendingValue)}</strong>
          <small>Pipeline requiring approval</small>
        </div>
      </div>

      {/* Active Filter Banner */}
      {((companyFilter && companyFilter !== "All Companies") || activeTab === "pending" || search || statusFilter !== "All" || typeFilter !== "All") && (
        <div className="active-filter-banner" data-testid="approvals-active-filter-banner">
          <div className="filter-title">
            <ShieldCheck size={15} />
            <span>Active Filter:</span>
            {activeTab === "pending" && (
              <span className="badge amber" style={{ fontWeight: 700 }} data-testid="active-filter-approvals-pending">
                Requests Awaiting Approval ({pendingRequests.length})
              </span>
            )}
            {activeTab === "history" && (
              <span className="badge blue" style={{ fontWeight: 700 }}>
                Approval History ({filteredHistory.length})
              </span>
            )}
            {companyFilter && companyFilter !== "All Companies" && (
              <span className="badge purple" style={{ fontWeight: 700 }}>
                Company: {companyFilter}
              </span>
            )}
            {statusFilter !== "All" && (
              <span className="badge gray" style={{ fontWeight: 600 }}>
                Status: {statusFilter}
              </span>
            )}
            {search && (
              <span className="badge gray" style={{ fontWeight: 500 }}>
                Search: &ldquo;{search}&rdquo;
              </span>
            )}
          </div>
          <button
            type="button"
            className="clear-btn"
            onClick={() => {
              setSearch("");
              setStatusFilter("All");
              setTypeFilter("All");
              setCompanyFilter("");
              if (window.history.pushState) window.history.pushState({}, "", "/approvals");
            }}
            data-testid="approvals-clear-filters-button"
          >
            <RotateCcw size={13} /> Reset filters
          </button>
        </div>
      )}

      {/* Toolbar: Navigation Tabs & Search & Filters */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          margin: "18px 0 16px",
        }}
      >
        {/* View Tabs */}
        <div style={{ display: "flex", gap: 6, background: "#f1f5f9", padding: 4, borderRadius: 8 }}>
          <button
            type="button"
            className={`outline-btn compact ${activeTab === "pending" ? "primary-btn" : ""}`}
            style={{
              borderRadius: 6,
              background: activeTab === "pending" ? "var(--navy, #0f172a)" : "transparent",
              color: activeTab === "pending" ? "#fff" : "#475569",
              borderColor: "transparent",
              fontWeight: 600,
            }}
            onClick={() => {
              setActiveTab("pending");
              setStatusFilter("All");
            }}
            data-testid="tab-pending-approvals"
          >
            Pending Review ({pendingRequests.length})
          </button>
          <button
            type="button"
            className={`outline-btn compact ${activeTab === "history" ? "primary-btn" : ""}`}
            style={{
              borderRadius: 6,
              background: activeTab === "history" ? "var(--navy, #0f172a)" : "transparent",
              color: activeTab === "history" ? "#fff" : "#475569",
              borderColor: "transparent",
              fontWeight: 600,
            }}
            onClick={() => {
              setActiveTab("history");
              setStatusFilter("All");
            }}
            data-testid="tab-history-approvals"
          >
            Approval History & Logs ({approvalsList.length})
          </button>
        </div>

        {/* Search & Filters */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative", minWidth: 260 }}>
            <Search size={15} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
            <input
              type="text"
              placeholder={activeTab === "pending" ? "Search batch, order, client, product..." : "Search approval ID, batch, order..."}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                pendingPagination.setCurrentPage(1);
                historyPagination.setCurrentPage(1);
              }}
              style={{ paddingLeft: 32, height: 36, fontSize: 13, width: "100%", borderRadius: 6 }}
              data-testid="approvals-search-input"
            />
          </div>

          {activeTab === "history" && (
            <>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  historyPagination.setCurrentPage(1);
                }}
                style={{ height: 36, fontSize: 13, borderRadius: 6 }}
                data-testid="approvals-status-filter"
              >
                <option value="All">All Decisions</option>
                <option value="Approved">Approved</option>
                <option value="Revision Required">Revision / Not Approved</option>
              </select>

              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  historyPagination.setCurrentPage(1);
                }}
                style={{ height: 36, fontSize: 13, borderRadius: 6 }}
                data-testid="approvals-type-filter"
              >
                <option value="All">All Types</option>
                <option value="In-House Approved">In-House Approved</option>
                <option value="Client Approved">Client Approved</option>
                <option value="Approved">Standard Approved</option>
                <option value="Not Approved">Not Approved</option>
              </select>
            </>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      {activeTab === "pending" ? (
        /* ---------------- PENDING APPROVALS QUEUE ---------------- */
        <section className="panel" data-testid="panel-pending-approvals">
          <div className="panel-title">
            <div>
              <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                Orders Awaiting Approval
                <span className="count">{filteredPending.length}</span>
              </h3>
              <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }}>
                Orders submitted from Design workflow awaiting internal review and sign-off.
              </p>
            </div>
          </div>

          {/* Bulk Selection Header / Active Bulk Action Panel */}
          {selectedRequestIds.size > 0 ? (
            <div className="approval-bulk-active-panel" data-testid="approval-bulk-active-panel">
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <input
                  type="checkbox"
                  className="approval-select-checkbox"
                  checked={isAllVisibleSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = hasSomeVisibleSelected;
                  }}
                  onChange={handleToggleSelectAll}
                  disabled={!canAct}
                  data-testid="checkbox-bulk-select-all"
                  title={isAllVisibleSelected ? "Deselect all visible requests" : "Select all visible requests"}
                />
                <div style={{ fontSize: 13, color: "#1e3a8a", fontWeight: 700 }}>
                  <span>{selectedRequestIds.size}</span> {selectedRequestIds.size === 1 ? "request" : "requests"} selected
                  <span style={{ fontWeight: 500, color: "#2563eb", marginLeft: 6 }}>
                    ({selectedProductsCount} products · {compactMoney(selectedTotalValue)})
                  </span>
                </div>
              </div>

              <div className="approval-bulk-toolbar">
                <button
                  type="button"
                  className="primary-btn compact"
                  style={{
                    background: "#16a34a",
                    borderColor: "#16a34a",
                    fontSize: 12.5,
                    fontWeight: 700,
                    padding: "6px 14px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                  onClick={() => setBulkApproveModalOpen(true)}
                  disabled={!canAct || isSubmittingBulk}
                  data-testid="btn-bulk-approve-selected"
                  title="Approve all selected requests together"
                >
                  <CheckCircle2 size={14} /> Approve Selected ({selectedRequestIds.size})
                </button>

                <button
                  type="button"
                  className="outline-btn compact"
                  style={{
                    color: "#dc2626",
                    borderColor: "#fca5a5",
                    fontSize: 12.5,
                    fontWeight: 700,
                    padding: "6px 14px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                  onClick={() => setBulkRejectModalOpen(true)}
                  disabled={!canAct || isSubmittingBulk}
                  data-testid="btn-bulk-reject-selected"
                  title="Reject all selected requests and request revisions"
                >
                  <RotateCcw size={14} /> Reject Selected ({selectedRequestIds.size})
                </button>

                <button
                  type="button"
                  className="clear-btn"
                  style={{ fontSize: 12, padding: "4px 8px" }}
                  onClick={handleClearSelection}
                  data-testid="btn-bulk-clear-selection"
                >
                  Deselect All
                </button>
              </div>
            </div>
          ) : filteredPending.length > 0 ? (
            <div className="approval-bulk-header-bar" data-testid="approval-bulk-header-bar">
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: canAct ? "pointer" : "not-allowed",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#475569",
                  margin: 0,
                }}
              >
                <input
                  type="checkbox"
                  className="approval-select-checkbox"
                  checked={false}
                  onChange={handleToggleSelectAll}
                  disabled={!canAct}
                  data-testid="checkbox-bulk-select-all"
                />
                <span>Select All ({filteredPending.length} pending requests)</span>
              </label>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="muted" style={{ fontSize: 12 }}>
                  {canAct
                    ? "Select requests to approve or reject in bulk"
                    : "Sign-off restricted to authorized managers"}
                </span>
                <button
                  type="button"
                  className="primary-btn compact"
                  style={{ fontSize: 12, opacity: 0.5, cursor: "not-allowed" }}
                  disabled
                  title="Select at least one request to approve"
                >
                  <CheckCircle2 size={13} /> Approve Selected
                </button>
                <button
                  type="button"
                  className="outline-btn compact"
                  style={{ fontSize: 12, opacity: 0.5, cursor: "not-allowed" }}
                  disabled
                  title="Select at least one request to reject"
                >
                  <RotateCcw size={13} /> Reject Selected
                </button>
              </div>
            </div>
          ) : null}

          <div className="approval-requests-list">
            {pendingPagination.paginatedItems.map((req) => {
              const isUrgent = req.priority === "Urgent";
              const isHigh = req.priority === "High";
              const isBusy = busyAction === req.id;
              const isSelected = selectedRequestIds.has(req.id);

              return (
                <div
                  key={req.id}
                  className={`approval-request-card ${isSelected ? "selected" : ""}`}
                  data-testid={`approval-pending-row-${req.id}`}
                >
                  {/* Top Header Row: Checkbox, Batch/Order ID, Bulk Badge, Created Date, Priority & Status */}
                  <div className="approval-card-header">
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      {/* Checkbox for Selecting this Request */}
                      <input
                        type="checkbox"
                        className="approval-select-checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleSelect(req.id);
                        }}
                        disabled={!canAct}
                        title={canAct ? `Select request #${req.id}` : "Sign-off restricted"}
                        data-testid={`checkbox-select-request-${req.id}`}
                        aria-label={`Select request ${req.id}`}
                      />

                      {req.isBulk ? (
                        <>
                          <span
                            className="mono"
                            style={{
                              background: "#eff6ff",
                              color: "#0284c7",
                              border: "1px solid #bae6fd",
                              padding: "3px 9px",
                              borderRadius: 5,
                              fontSize: 13,
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              if (onOpenDesign) onOpenDesign(req.orders[0]?.order_id);
                              else if (onOpen) onOpen(req.orders[0]?.order_id);
                            }}
                            title={`Open Bulk Request #${req.batchId}`}
                          >
                            #{req.batchId}
                          </span>
                          <span
                            className="badge blue"
                            style={{
                              fontSize: 11,
                              padding: "3px 8px",
                              fontWeight: 700,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <Layers size={12} /> Bulk Request · {req.orders.length} Products
                          </span>
                        </>
                      ) : (
                        <>
                          <span
                            className="mono"
                            style={{
                              background: "#eff6ff",
                              color: "#0284c7",
                              border: "1px solid #bae6fd",
                              padding: "3px 9px",
                              borderRadius: 5,
                              fontSize: 13,
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                            onClick={() => onOpen && onOpen(req.order_id)}
                            title="Open Order Detail"
                          >
                            #{req.order_id}
                          </span>
                          <span
                            className="badge gray"
                            style={{ fontSize: 11, padding: "3px 8px", fontWeight: 600 }}
                          >
                            Single Order
                          </span>
                        </>
                      )}

                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        · Created {req.order_date || "—"}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span className={`badge ${isUrgent ? "red" : isHigh ? "amber" : "blue"}`} style={{ fontSize: 11 }}>
                        {req.priority || "Normal"}
                      </span>

                      {/* Current Approval Status Badge (Separated from Actions) */}
                      <span
                        className="badge amber"
                        style={{
                          fontSize: 11.5,
                          fontWeight: 700,
                          padding: "3px 10px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <Clock size={12} /> Pending Review
                      </span>
                    </div>
                  </div>

                  {/* Body Section: 3 Clear, Proportional Columns */}
                  <div className="approval-card-body">
                    {/* Column 1: Client & Metadata */}
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          color: "#64748b",
                          marginBottom: 3,
                        }}
                      >
                        Client Information
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", lineHeight: 1.25 }}>
                        {req.client_name || "—"}
                      </div>
                      <div style={{ fontSize: 12, color: "#475569", marginTop: 4, lineHeight: 1.4 }}>
                        {req.client_contact_person || "Contact via order"}
                        {canViewClientPhone(user) && req.client_phone ? ` · 📞 ${req.client_phone}` : ""}
                      </div>

                      <div
                        style={{
                          marginTop: 12,
                          display: "flex",
                          flexDirection: "column",
                          gap: 5,
                          fontSize: 12,
                          color: "#334155",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span className="muted" style={{ minWidth: 85 }}>Design Proof:</span>
                          <span className="badge purple" style={{ fontSize: 11, fontWeight: 700, padding: "1px 6px" }}>
                            V{req.design_version || 1}
                          </span>
                          {req.assigned_designer && (
                            <span className="muted" style={{ fontSize: 11.5 }}>
                              by {req.assigned_designer}
                            </span>
                          )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span className="muted" style={{ minWidth: 85 }}>Required Due:</span>
                          <span style={{ fontWeight: isUrgent ? 700 : 600, color: isUrgent ? "#dc2626" : "#1e293b" }}>
                            📅 {req.required_delivery_date || "—"}
                          </span>
                        </div>
                        {req.company && (
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span className="muted" style={{ minWidth: 85 }}>Company:</span>
                            <span style={{ fontWeight: 500, color: "#475569" }}>
                              {req.company}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Column 2: Products & Quantities Breakdown (Wide & Spacious) */}
                    <div className="approval-products-box">
                      <div className="approval-products-header">
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#1e293b",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <Package size={14} color="#0284c7" />
                          {req.isBulk ? `Included Products (${req.orders.length})` : "Product Details"}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
                          Total: {req.total_quantity.toLocaleString()} {req.orders[0]?.unit || "units"}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          maxHeight: 220,
                          overflowY: "auto",
                        }}
                      >
                        {req.orders.map((sub) => (
                          <div key={sub.order_id} className="approval-product-row">
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                minWidth: 0,
                                flex: 1,
                                paddingRight: 12,
                              }}
                            >
                              <span style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1 }}>•</span>
                              <b
                                style={{
                                  color: "#1e293b",
                                  fontSize: 13,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={sub.product}
                              >
                                {sub.product}
                              </b>
                              <span
                                className="mono"
                                style={{
                                  fontSize: 11,
                                  color: "#0284c7",
                                  background: "#eff6ff",
                                  padding: "1px 6px",
                                  borderRadius: 4,
                                  border: "1px solid #dbeafe",
                                  cursor: "pointer",
                                  whiteSpace: "nowrap",
                                }}
                                onClick={() => onOpen && onOpen(sub.order_id)}
                                title="Open order detail"
                              >
                                #{sub.order_id}
                              </span>
                            </div>
                            <div style={{ fontWeight: 600, color: "#334155", fontSize: 12.5, whiteSpace: "nowrap" }}>
                              {(sub.quantity || 0).toLocaleString()} {sub.unit || "pcs"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Column 3: Total Request Value & Action Buttons */}
                    <div className="approval-actions-column">
                      <div style={{ textAlign: "right", width: "100%" }}>
                        <div
                          style={{
                            fontSize: 11,
                            textTransform: "uppercase",
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            color: "#64748b",
                          }}
                        >
                          Total Request Value
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", marginTop: 2 }}>
                          {compactMoney(req.total_value)}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                          width: "100%",
                          alignItems: "flex-end",
                          marginTop: 14,
                        }}
                      >
                        {canAct ? (
                          <div className="approval-action-buttons">
                            <button
                              type="button"
                              className="primary-btn compact"
                              style={{
                                background: "#16a34a",
                                borderColor: "#16a34a",
                                fontSize: 12.5,
                                fontWeight: 700,
                                padding: "6px 16px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                              onClick={() => (req.isBulk ? handleApproveBatch(req.batchId) : handleApproveOrder(req.order_id))}
                              disabled={isBusy}
                              data-testid={`btn-approve-yes-${req.id}`}
                              title={
                                req.isBulk
                                  ? `Approve Request #${req.batchId} (All ${req.orders.length} products) — Unlocks Production`
                                  : "Approve Design (Yes) — Unlocks Production"
                              }
                            >
                              <CheckCircle2 size={14} /> Yes
                            </button>
                            <button
                              type="button"
                              className="outline-btn compact"
                              style={{
                                color: "#dc2626",
                                borderColor: "#fca5a5",
                                fontSize: 12.5,
                                fontWeight: 700,
                                padding: "6px 16px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                              onClick={() => (req.isBulk ? handleRejectBatch(req.batchId) : handleRejectOrder(req.order_id))}
                              disabled={isBusy}
                              data-testid={`btn-approve-no-${req.id}`}
                              title={
                                req.isBulk
                                  ? `Reject Request #${req.batchId} (Return all ${req.orders.length} products for revisions)`
                                  : "Reject Design (No) — Return for revisions"
                              }
                            >
                              <RotateCcw size={14} /> No
                            </button>
                          </div>
                        ) : (
                          <div className="muted" style={{ fontSize: 11.5, fontStyle: "italic", textAlign: "right" }}>
                            Sign-off restricted
                          </div>
                        )}

                        {/* View workspace / details links */}
                        {req.isBulk ? (
                          <button
                            type="button"
                            className="outline-btn compact"
                            style={{ fontSize: 11.5, padding: "4px 10px", color: "#475569" }}
                            onClick={() => {
                              if (onOpenDesign) onOpenDesign(req.orders[0]?.order_id);
                              else if (onOpen) onOpen(req.orders[0]?.order_id);
                            }}
                            data-testid={`btn-view-design-${req.id}`}
                            title="Open Bulk Design Workspace"
                          >
                            <Palette size={13} /> Open Bulk Workspace
                          </button>
                        ) : (
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              type="button"
                              className="outline-btn compact"
                              style={{ fontSize: 11.5, padding: "4px 8px", color: "#475569" }}
                              onClick={() => onOpenDesign && onOpenDesign(req.order_id)}
                              data-testid={`btn-view-design-${req.id}`}
                              title="Open Design Detail"
                            >
                              <Palette size={13} /> Design
                            </button>
                            <button
                              type="button"
                              className="outline-btn compact"
                              style={{ fontSize: 11.5, padding: "4px 8px", color: "#475569" }}
                              onClick={() => onOpen && onOpen(req.order_id)}
                              data-testid={`btn-view-order-${req.id}`}
                              title="Open Order Detail"
                            >
                              <ExternalLink size={13} /> Order
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredPending.length === 0 && (
              <div
                className="empty-inline"
                style={{
                  padding: "40px 16px",
                  textAlign: "center",
                  background: "#f8fafc",
                  borderRadius: 8,
                  border: "1px dashed #cbd5e1",
                }}
              >
                <CheckCircle2 size={36} color="#059669" style={{ marginBottom: 8 }} />
                <h4 style={{ margin: "4px 0", color: "#1e293b", fontSize: 15 }}>
                  {search ? "No pending approvals match your search filter." : "No orders awaiting approval."}
                </h4>
                <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                  All design artwork is either in progress or has already been signed off.
                </p>
              </div>
            )}
          </div>

          {pendingPagination.totalItems > 0 && (
            <Pagination
              currentPage={pendingPagination.currentPage}
              totalItems={pendingPagination.totalItems}
              pageSize={pendingPagination.pageSize}
              onPageChange={pendingPagination.setCurrentPage}
              onPageSizeChange={pendingPagination.setPageSize}
              itemLabel="pending requests"
            />
          )}
        </section>
      ) : (
        /* ---------------- APPROVAL HISTORY & AUDIT LOG ---------------- */
        <section className="panel" data-testid="panel-history-approvals">
          <div className="panel-title">
            <div>
              <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                Approval History & Audit Logs
                <span className="count">{filteredHistory.length}</span>
              </h3>
              <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }}>
                Comprehensive ledger of internal review decisions logged across orders and batches.
              </p>
            </div>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Approval ID</th>
                  <th style={{ width: 150 }}>Order / Batch</th>
                  <th>Client & Product</th>
                  <th style={{ width: 120 }}>Decision</th>
                  <th style={{ width: 150 }}>Sign-off Type</th>
                  <th style={{ width: 140 }}>Approver</th>
                  <th style={{ width: 130 }}>Date & Time</th>
                  <th>Comments / Remarks</th>
                  <th style={{ textAlign: "right", width: 110 }}>View</th>
                </tr>
              </thead>
              <tbody>
                {historyPagination.paginatedItems.map((a) => {
                  const isApproved = a.status === "Approved";
                  const isRevision = a.status === "Revision Required" || a.status === "Not Approved" || a.status === "Rejected";
                  const isInHouse = (a.approval_type || "").includes("In-House");
                  const isClient = (a.approval_type || "").includes("Client");

                  return (
                    <tr key={a.approval_id || `${a.order_id}-${a.created_at}`} data-testid={`approval-history-row-${a.approval_id}`}>
                      {/* Approval ID */}
                      <td>
                        <b className="mono" style={{ fontSize: 12, color: "#64748b" }}>
                          {a.approval_id || "AP-—"}
                        </b>
                      </td>

                      {/* Order / Batch ID */}
                      <td>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <b
                            className="mono"
                            style={{ color: "#0284c7", cursor: "pointer", fontSize: 12.5 }}
                            onClick={() => onOpen && onOpen(a.order_id)}
                            title="Open Order Detail"
                          >
                            {a.order_id}
                          </b>
                          {a.upload_batch_id && (
                            <span
                              className="badge blue"
                              style={{ fontSize: 10, padding: "1px 5px", width: "fit-content" }}
                              title={`Bulk Request Batch #${a.upload_batch_id}`}
                            >
                              #{a.upload_batch_id.replace(/^BATCH-/, "")}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Client & Product */}
                      <td>
                        <div>
                          <b style={{ color: "#1e293b", fontSize: 12.5 }}>{a.client_name || "—"}</b>
                          <small className="muted" style={{ display: "block", fontSize: 11.5 }}>
                            {a.product || "—"}
                          </small>
                        </div>
                      </td>

                      {/* Status */}
                      <td>
                        <span className={`badge ${isApproved ? "green" : isRevision ? "red" : "amber"}`}>
                          {a.status}
                        </span>
                      </td>

                      {/* Approval Type */}
                      <td>
                        {a.approval_type ? (
                          <span
                            className={`badge ${isInHouse ? "blue" : isClient ? "green" : "purple"}`}
                            style={{ fontSize: 11, padding: "2px 8px", fontWeight: 600 }}
                          >
                            {isInHouse ? "🏢 " : isClient ? "🤝 " : ""}
                            {a.approval_type}
                          </span>
                        ) : (
                          <span className="muted" style={{ fontSize: 11 }}>—</span>
                        )}
                      </td>

                      {/* Approver */}
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <User size={13} color="#64748b" />
                          <span style={{ fontSize: 12.5, fontWeight: 600, color: "#334155" }}>
                            {a.approver || "Team"}
                          </span>
                        </div>
                      </td>

                      {/* Date */}
                      <td>
                        <small className="muted" style={{ fontSize: 11.5 }}>
                          {a.created_at ? new Date(a.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : "—"}
                        </small>
                      </td>

                      {/* Comments */}
                      <td>
                        <span
                          style={{
                            display: "inline-block",
                            maxWidth: 240,
                            fontSize: 12,
                            color: "#475569",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={a.comments || "No comments"}
                        >
                          {a.comments || "—"}
                        </span>
                      </td>

                      {/* View Link */}
                      <td style={{ textAlign: "right" }}>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
                          <button
                            type="button"
                            className="outline-btn compact"
                            style={{ fontSize: 11.5, padding: "3px 8px" }}
                            onClick={() => onOpenDesign && onOpenDesign(a.order_id)}
                            title="Open Design Detail"
                          >
                            <Palette size={12} /> Design
                          </button>
                          <button
                            type="button"
                            className="outline-btn compact icon-btn"
                            style={{ padding: "3px 6px" }}
                            onClick={() => onOpen && onOpen(a.order_id)}
                            title="Open Order Detail"
                          >
                            <ExternalLink size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {filteredHistory.length === 0 && (
              <div className="empty-inline" style={{ padding: "36px 16px", textAlign: "center" }}>
                <p style={{ fontWeight: 600, margin: "4px 0", color: "#1e293b" }}>No approval records match the criteria.</p>
                <small className="muted">Past approvals and sign-off decisions will be logged here automatically.</small>
              </div>
            )}
          </div>

          {historyPagination.totalItems > 0 && (
            <Pagination
              currentPage={historyPagination.currentPage}
              totalItems={historyPagination.totalItems}
              pageSize={historyPagination.pageSize}
              onPageChange={historyPagination.setCurrentPage}
              onPageSizeChange={historyPagination.setPageSize}
              itemLabel="approval records"
            />
          )}
        </section>
      )}

      {/* ---------------- BULK APPROVE CONFIRMATION MODAL ---------------- */}
      {bulkApproveModalOpen && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10000 }}
          data-testid="modal-bulk-approve-backdrop"
          onClick={() => !isSubmittingBulk && setBulkApproveModalOpen(false)}
        >
          <div
            className="modal"
            style={{ width: "min(560px, 95vw)" }}
            data-testid="modal-bulk-approve"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-head">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "#dcfce7",
                    display: "grid",
                    placeItems: "center",
                    color: "#16a34a",
                    flexShrink: 0,
                  }}
                >
                  <CheckCircle2 size={24} />
                </div>
                <div>
                  <h3 style={{ margin: 0, color: "#0f172a", fontSize: 18 }}>
                    Approve Selected Requests
                  </h3>
                  <p className="muted" style={{ margin: "2px 0 0", fontSize: 12.5 }}>
                    Production stage unlocks immediately for all approved products.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="mobile-close"
                style={{ display: "block" }}
                onClick={() => !isSubmittingBulk && setBulkApproveModalOpen(false)}
                disabled={isSubmittingBulk}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ margin: "14px 0" }}>
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 8,
                  padding: "12px 16px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>
                    {selectedRequestIds.size} {selectedRequestIds.size === 1 ? "Request" : "Requests"} to be Approved
                  </div>
                  <div style={{ fontSize: 12, color: "#15803d", marginTop: 2 }}>
                    Total of {selectedProductsCount} products across {new Set(selectedRequestsList.map((r) => r.client_name)).size} client(s)
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: "#166534", textTransform: "uppercase", fontWeight: 700 }}>
                    Total Value
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#14532d" }}>
                    {compactMoney(selectedTotalValue)}
                  </div>
                </div>
              </div>

              {/* Selected Requests Breakdown List */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Selected Requests Breakdown
                </div>
                <div className="approval-modal-items-list">
                  {selectedRequestsList.map((r) => (
                    <div key={r.id} className="approval-modal-item-row">
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1, paddingRight: 8 }}>
                        <span className="mono" style={{ fontWeight: 700, color: "#0284c7", fontSize: 12 }}>
                          #{r.batchId || r.order_id}
                        </span>
                        <span style={{ color: "#334155", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {r.client_name}
                        </span>
                        <span className="badge blue" style={{ fontSize: 10.5, padding: "1px 5px", flexShrink: 0 }}>
                          {r.orders.length} {r.orders.length === 1 ? "product" : "products"}
                        </span>
                      </div>
                      <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 12.5, whiteSpace: "nowrap" }}>
                        {compactMoney(r.total_value)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Optional Approval Notes */}
              <label className="full" style={{ marginTop: 14 }}>
                Approval Notes / Sign-off Comments (Optional)
                <input
                  type="text"
                  placeholder="e.g. Design verified and approved for production"
                  value={bulkComments}
                  onChange={(e) => setBulkComments(e.target.value)}
                  disabled={isSubmittingBulk}
                  data-testid="input-bulk-approve-comments"
                />
              </label>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={() => setBulkApproveModalOpen(false)}
                disabled={isSubmittingBulk}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                style={{ background: "#16a34a", borderColor: "#16a34a", fontWeight: 700 }}
                onClick={handleExecuteBulkApprove}
                disabled={isSubmittingBulk}
                data-testid="btn-confirm-bulk-approve"
              >
                {isSubmittingBulk ? "Approving..." : `Confirm & Approve (${selectedRequestIds.size})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- BULK REJECT CONFIRMATION MODAL ---------------- */}
      {bulkRejectModalOpen && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 10000 }}
          data-testid="modal-bulk-reject-backdrop"
          onClick={() => !isSubmittingBulk && setBulkRejectModalOpen(false)}
        >
          <div
            className="modal"
            style={{ width: "min(560px, 95vw)" }}
            data-testid="modal-bulk-reject"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-head">
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "#fee2e2",
                    display: "grid",
                    placeItems: "center",
                    color: "#dc2626",
                    flexShrink: 0,
                  }}
                >
                  <AlertTriangle size={24} />
                </div>
                <div>
                  <h3 style={{ margin: 0, color: "#0f172a", fontSize: 18 }}>
                    Reject Selected Requests
                  </h3>
                  <p className="muted" style={{ margin: "2px 0 0", fontSize: 12.5 }}>
                    Orders will be returned to Design (In Progress) for revisions.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="mobile-close"
                style={{ display: "block" }}
                onClick={() => !isSubmittingBulk && setBulkRejectModalOpen(false)}
                disabled={isSubmittingBulk}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ margin: "14px 0" }}>
              <div
                style={{
                  background: "#fff1f2",
                  border: "1px solid #fecdd3",
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginBottom: 14,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: "#9f1239" }}>
                  Returning {selectedRequestIds.size} {selectedRequestIds.size === 1 ? "Request" : "Requests"} ({selectedProductsCount} products) for revisions
                </div>
                <div style={{ fontSize: 12, color: "#be123c", marginTop: 3 }}>
                  These requests will be removed from Pending Review and assigned designers will see them in their Design queue with your revision notes.
                </div>
              </div>

              {/* Selected Requests Breakdown List */}
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Selected Requests Breakdown
                </div>
                <div className="approval-modal-items-list">
                  {selectedRequestsList.map((r) => (
                    <div key={r.id} className="approval-modal-item-row">
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1, paddingRight: 8 }}>
                        <span className="mono" style={{ fontWeight: 700, color: "#0284c7", fontSize: 12 }}>
                          #{r.batchId || r.order_id}
                        </span>
                        <span style={{ color: "#334155", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {r.client_name}
                        </span>
                        <span className="badge amber" style={{ fontSize: 10.5, padding: "1px 5px", flexShrink: 0 }}>
                          {r.orders.length} {r.orders.length === 1 ? "product" : "products"}
                        </span>
                      </div>
                      <div style={{ fontWeight: 600, color: "#64748b", fontSize: 12, whiteSpace: "nowrap" }}>
                        V{r.design_version || 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Required Rejection Reason */}
              <label className="full" style={{ marginTop: 14 }}>
                <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Rejection Reason / Revision Notes <span style={{ color: "#dc2626" }}>*</span></span>
                  <span style={{ fontWeight: 400, color: "#64748b", fontSize: 11 }}>Required</span>
                </span>
                <textarea
                  rows={3}
                  placeholder="Explain why these requests are being returned and what revisions are needed..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  disabled={isSubmittingBulk}
                  data-testid="input-bulk-reject-reason"
                  style={{ resize: "vertical", marginTop: 6 }}
                />
              </label>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
              <button
                type="button"
                className="outline-btn"
                onClick={() => setBulkRejectModalOpen(false)}
                disabled={isSubmittingBulk}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                style={{ background: "#dc2626", borderColor: "#dc2626", fontWeight: 700 }}
                onClick={handleExecuteBulkReject}
                disabled={isSubmittingBulk || !rejectionReason.trim()}
                data-testid="btn-confirm-bulk-reject"
              >
                {isSubmittingBulk ? "Rejecting..." : `Confirm Rejection (${selectedRequestIds.size})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
